import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchRoles, fetchStaff, createStaff, updateStaff, fetchStaffHistory } from "../api/api";
import { useAuth } from "../context/AuthContext";

const EMPTY_FORM = {
  full_name: "",
  role_id: "",
  email: "",
  mobile: "",
};

const FIELD_LABELS = {
  full_name: "Name",
  email: "Email",
  alternate_email: "Alternate Email",
  mobile: "Mobile Number",
  landline_number: "Landline Number",
  is_active: "Active Status",
  role: "Role",
};

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function errorMessage(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (status === 409) return detail || "A user with this mobile number or email already exists.";
  if (status === 404) return detail || "Selected role was not found.";
  if (status === 422) return detail || "Please check the form — some fields are invalid or missing.";
  return "Something went wrong. Please try again.";
}

function formatHistoryValue(fieldName, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (fieldName === "is_active") return value === "True" ? "Active" : "Inactive";
  return value;
}

function Staff() {

  const { user: currentUser } = useAuth();
  const [showForm, setShowForm] = useState(false);

  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");

  const [historyId, setHistoryId] = useState(null);
  const [historyByStaffId, setHistoryByStaffId] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);


  // =========================
  // LOAD ROLES (for the dropdown — everything except "customer")
  // =========================

  useEffect(() => {
    async function loadRoles() {
      try {
        const data = await fetchRoles();
        setRoles(data.filter((r) => r.name.toLowerCase() !== "customer"));
      } catch (err) {
        setRolesError("Could not load roles. Please refresh and try again.");
      }
    }
    loadRoles();
  }, []);


  // =========================
  // LOAD STAFF / SUPERVISOR LIST
  // =========================

  const loadStaff = async () => {
    setStaffLoading(true);
    setStaffError("");
    try {
      const data = await fetchStaff();
      setStaffList(data);
    } catch (err) {
      setStaffError(errorMessage(err));
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);


  // =========================
  // ADD STAFF / SUPERVISOR
  // =========================

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.role_id) {
      setError("Please select a role.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await createStaff({
        full_name: form.full_name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim() || undefined,
        role_id: Number(form.role_id),
      });

      setSuccessMessage(
        data.temporary_password
          ? `Added. No email on file — temporary password: ${data.temporary_password}`
          : "Added. Welcome email with login details has been sent."
      );

      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadStaff();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };


  // =========================
  // EDIT STAFF
  // =========================

  const startEdit = (staff) => {
    setHistoryId(null);
    setEditError("");
    setEditingId(staff.id);
    setEditForm({
      full_name: staff.full_name || "",
      email: staff.email || "",
      mobile: staff.mobile || "",
      role_id: staff.role?.id || "",
      is_active: staff.is_active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setEditError("");
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const saveEdit = async (staffId) => {
    setEditSubmitting(true);
    setEditError("");
    try {
      await updateStaff(staffId, {
        full_name: editForm.full_name.trim(),
        email: editForm.email.trim() || null,
        mobile: editForm.mobile.trim(),
        role_id: editForm.role_id ? Number(editForm.role_id) : null,
        is_active: editForm.is_active,
      });
      setEditingId(null);
      setEditForm(null);
      await loadStaff();
      // Invalidate any cached history for this row so re-opening it
      // re-fetches and shows the edit that was just made.
      setHistoryByStaffId((prev) => {
        const next = { ...prev };
        delete next[staffId];
        return next;
      });
    } catch (err) {
      setEditError(errorMessage(err));
    } finally {
      setEditSubmitting(false);
    }
  };


  // =========================
  // EDIT HISTORY
  // =========================

  const toggleHistory = async (staffId) => {
    if (historyId === staffId) {
      setHistoryId(null);
      return;
    }
    cancelEdit();
    setHistoryId(staffId);
    if (historyByStaffId[staffId]) return;
    setHistoryLoadingId(staffId);
    try {
      const data = await fetchStaffHistory(staffId);
      setHistoryByStaffId((prev) => ({ ...prev, [staffId]: data }));
    } catch (err) {
      setHistoryByStaffId((prev) => ({ ...prev, [staffId]: [] }));
    } finally {
      setHistoryLoadingId(null);
    }
  };


  return (
    <>

    <Navbar />

    <Sidebar />

    <div className="admin-content">


      <div className="staff-page">

        {/* HEADER */}
        <div className="staff-header">
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Staff management</h2>

          <button className="add-staff-btn" onClick={() => setShowForm(true)}>
            + Add staff
          </button>
        </div>

        {rolesError && (
          <p className="login-error" style={{ color: "red" }}>
            {rolesError}
          </p>
        )}

        {successMessage && (
          <p style={{ color: "#2e7d32", fontWeight: 600 }}>
            {successMessage}
          </p>
        )}

{showForm && (
  <div className="staff-form-card">

    <h3>Add Staff / Supervisor</h3>

    {error && (
      <p className="login-error" style={{ color: "red" }}>
        {error}
      </p>
    )}

    <label>Name</label>
    <input
      type="text"
      name="full_name"
      value={form.full_name}
      onChange={handleChange}
      required
    />

    <label>Role</label>
    <select
      name="role_id"
      value={form.role_id}
      onChange={handleChange}
      required
    >
      <option value="">Select a role</option>
      {roles.map((role) => (
        <option key={role.id} value={role.id}>
          {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
        </option>
      ))}
    </select>

    <label>Email</label>
    <input
      type="email"
      name="email"
      value={form.email}
      onChange={handleChange}
    />

    <label>Mobile Number</label>
    <input
      type="text"
      name="mobile"
      value={form.mobile}
      onChange={handleChange}
      required
    />

    <div className="staff-form-actions">

      <button
        className="approve-btn"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? "Saving…" : "Save"}
      </button>

      <button
        className="reject-btn"
        onClick={() => {
          setShowForm(false);
          setForm(EMPTY_FORM);
          setError("");
        }}
      >
        Cancel
      </button>

    </div>

  </div>
)}


        {/* STAFF TABLE */}
        <div className="staff-table-card">

          {staffError && (
            <p className="login-error" style={{ color: "red" }}>
              {staffError}
            </p>
          )}

          {staffLoading && <p>Loading…</p>}

          <table className="staff-table">

            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {!staffLoading && staffList.length === 0 && (
                <tr>
                  <td colSpan={6}>No staff or supervisors added yet.</td>
                </tr>
              )}

              {staffList.map((staff) => {
                const isSelf = currentUser?.id === staff.id;
                const isEditing = editingId === staff.id;
                const isHistoryOpen = historyId === staff.id;
                return (
                <React.Fragment key={staff.id}>
                <tr>
                  <td>
                    <div className="staff-name-box">
                      <div className="avatar">
                        {initials(staff.full_name)}
                      </div>
                      <span>{staff.full_name}</span>
                    </div>
                  </td>

                  <td>{staff.role?.name || "—"}</td>
                  <td>{staff.email || "—"}</td>
                  <td>{staff.mobile}</td>

                  <td>
                    <span className={staff.is_active ? "badge active" : "badge"}>
                      {staff.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        className="view-btn"
                        onClick={() => (isEditing ? cancelEdit() : startEdit(staff))}
                      >
                        {isEditing ? "Cancel" : "Edit"}
                      </button>
                      <button
                        className="view-btn"
                        onClick={() => toggleHistory(staff.id)}
                      >
                        {isHistoryOpen ? "Hide History" : "History"}
                      </button>
                    </div>
                  </td>
                </tr>

                {isEditing && (
                  <tr>
                    <td colSpan={6} style={{ background: "#fafafa", padding: "15px" }}>

                      {editError && <p className="login-error" style={{ color: "red" }}>{editError}</p>}

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>

                        <label>
                          Name
                          <input type="text" name="full_name" value={editForm.full_name} onChange={handleEditChange} />
                        </label>

                        <label>
                          Role
                          <select name="role_id" value={editForm.role_id} onChange={handleEditChange}>
                            <option value="">Select a role</option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label>
                          Email
                          <input type="email" name="email" value={editForm.email} onChange={handleEditChange} />
                        </label>

                        <label>
                          Mobile Number
                          <input type="text" name="mobile" value={editForm.mobile} onChange={handleEditChange} />
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: "8px" }} title={isSelf ? "You cannot change your own active status" : undefined}>
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={editForm.is_active}
                            onChange={handleEditChange}
                            disabled={isSelf}
                          />
                          Active
                          {isSelf && <span style={{ color: "#888", fontSize: "12px" }}>(can't change your own status)</span>}
                        </label>

                      </div>

                      <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                        <button
                          type="button"
                          className="login-btn"
                          style={{ width: "auto", padding: "8px 16px" }}
                          onClick={() => saveEdit(staff.id)}
                          disabled={editSubmitting}
                        >
                          {editSubmitting ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="register-btn"
                          style={{ width: "auto", padding: "8px 16px" }}
                          onClick={cancelEdit}
                          disabled={editSubmitting}
                        >
                          Cancel
                        </button>
                      </div>

                    </td>
                  </tr>
                )}

                {isHistoryOpen && (
                  <tr>
                    <td colSpan={6} style={{ background: "#fafafa", padding: "15px" }}>
                      <p style={{ fontWeight: 700, marginBottom: "8px" }}>Edit History</p>
                      {historyLoadingId === staff.id && <p>Loading…</p>}
                      {historyLoadingId !== staff.id && (historyByStaffId[staff.id] || []).length === 0 && (
                        <p>No edits recorded yet.</p>
                      )}
                      {historyLoadingId !== staff.id && (historyByStaffId[staff.id] || []).map((h) => (
                        <div key={h.id} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #eee" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <strong>{FIELD_LABELS[h.field_name] || h.field_name}</strong>
                            <span style={{ fontSize: "12px", color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                          </div>
                          <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#555" }}>
                            {formatHistoryValue(h.field_name, h.old_value)} → {formatHistoryValue(h.field_name, h.new_value)}
                          </p>
                          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#888" }}>
                            Changed by {h.changed_by_name || "—"}
                          </p>
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>

          </table>

        </div>

      </div>

    </div>

    </>

  );

}

export default Staff;
